import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { AdminMe, AdminRole, SupervisorMe } from '@neon-one/auctions-types';
import { EvaluatedClientFlagsSchema, type EvaluatedClientFlags } from '@neon-one/auctions-types';

import { httpClient } from '@/shell/http';
import { isJwtExpired, decodeJwtPayload } from '@/shell/auth/loadAdminUser';
import {
  readSessionCookie,
  writeSessionCookie,
  clearSessionCookie,
  readSupervisorRootCookie,
  writeSupervisorRootCookie,
  clearSupervisorRootCookie,
  type SessionCookie,
  type SupervisorRootCookie,
} from '@/shell/auth/sessionCookies';

// Legacy per-origin localStorage keys (pre parent-domain-cookie sessions).
// Purged on store init so stale copies — the source of the
// logout-doesn't-propagate bug — cannot resurrect an identity.
const LEGACY_SESSION_KEY = 'neon-auctions:admin-session';
const LEGACY_SUPERVISOR_KEY = 'neon-auctions:supervisor-session';

function readSession(): SessionCookie | null {
  const s = readSessionCookie();
  if (!s?.jwt || isJwtExpired(s.jwt)) {
    if (s) clearSessionCookie();
    return null;
  }
  return s;
}

function readRoot(): SupervisorRootCookie | null {
  const s = readSupervisorRootCookie();
  if (!s?.jwt || isJwtExpired(s.jwt)) {
    if (s) clearSupervisorRootCookie();
    return null;
  }
  return s;
}

export const useAdminSession = defineStore('adminSession', () => {
  try {
    localStorage.removeItem(LEGACY_SESSION_KEY);
    localStorage.removeItem(LEGACY_SUPERVISOR_KEY);
  } catch {
    // best-effort
  }

  let initial = readSession();
  let initialRoot = readRoot();

  // Consistency check: a regular (non-supervisor) session coexisting with a
  // supervisor root violates the write invariants (regular setSession clears
  // the root; setSupervisorSession clears the session) — it means one of the
  // two is stale, e.g. left behind by an old bundle whose logout predates
  // cookie sessions. Keep the identity issued most recently and purge the
  // other, so a stale login can never shadow a newer one.
  if (initial && initialRoot && decodeJwtPayload(initial.jwt)?.is_supervisor !== true) {
    const sessionIat = decodeJwtPayload(initial.jwt)?.iat ?? 0;
    const rootIat = decodeJwtPayload(initialRoot.jwt)?.iat ?? 0;
    if (rootIat > sessionIat) {
      clearSessionCookie();
      initial = null;
    } else {
      clearSupervisorRootCookie();
      initialRoot = null;
    }
  }
  const jwt = ref<string | null>(initial?.jwt ?? initialRoot?.jwt ?? null);
  const user = ref<AdminMe | null>(initial?.user ?? null);
  // Populated only when the supervisor identity is the ACTIVE one (at the
  // lobby); while assumed into an org, user.isSupervisor carries the flag
  // and the root identity waits in its own cookie.
  const supervisorUser = ref<SupervisorMe | null>(initial ? null : initialRoot?.supervisorUser ?? null);

  const isAuthenticated = computed(() => jwt.value !== null);
  const role = computed<AdminRole | null>(() => user.value?.role ?? null);
  const orgId = computed(() => user.value?.orgId ?? null);
  const isSupervisor = computed(() => supervisorUser.value !== null || user.value?.isSupervisor === true);
  // Only sessions bound through the multi-org picker carry `multi_org`.
  // Supervisors switch orgs from the lobby, never through SSO re-selection.
  const canSwitchOrg = computed(
    () => !isSupervisor.value && jwt.value !== null && decodeJwtPayload(jwt.value)?.multi_org === true,
  );

  // Evaluated, org-scoped client feature flags for the signed-in admin's own
  // org (GET /admin/feature-flags). Every gated boolean DEFAULTS TO `false`
  // until loaded and on any error, preserving hidden-by-default behavior — a
  // flag-fetch failure must never break navigation or reveal a dark feature.
  // loadFeatureFlags() is idempotent (skips when already loaded/loading) and
  // swallows all errors. The flag cache is org-scoped, so every identity
  // transition (setSession / setSupervisorSession / restoreSupervisorRoot /
  // clear) must call resetFlags() — that both drops the stale cache AND bumps
  // flagsToken so an in-flight load for the outgoing org can't write its result
  // (or leave flagsLoaded=true, which would block the new org's own fetch).
  const flags = ref<EvaluatedClientFlags | null>(null);
  const flagsLoaded = ref(false);
  const flagsLoading = ref(false);
  // Version token guarding against a flag load resolving after the identity
  // it was issued for has changed. Not reactive — a plain closure counter.
  let flagsToken = 0;

  function resetFlags(): void {
    flagsToken += 1;
    flags.value = null;
    flagsLoaded.value = false;
    flagsLoading.value = false;
  }

  // Shared fetch+parse+assign for the org-scoped client flags. Mints a fresh
  // flagsToken per fetch; if it changes mid-flight (a newer fetch was issued, or
  // the identity moved on via resetFlags) the response is discarded. Never
  // throws — a flag fetch failure must not break navigation (safe defaults
  // stay in place).
  async function fetchFlagsForToken(fresh = false): Promise<void> {
    // Flags are evaluated per org. A supervisor in the lobby has no org (and
    // a role-less lobby token), so the endpoint refuses with 403
    // ROLE_INSUFFICIENT and the shell surfaces it as a "Permission denied"
    // toast. The WS flag broadcast and reconnect refresh both reach here.
    if (supervisorUser.value !== null && user.value === null) return;
    const token = ++flagsToken;
    flagsLoading.value = true;
    try {
      // `?fresh=1` tells the backend to bypass its 15s per-container flag cache
      // for this read. Used only by reloadFeatureFlags() (WS-driven), so a
      // scheduled/live flip reflects immediately instead of waiting out the TTL;
      // the idempotent navigation load stays on the cached path.
      const url = fresh ? '/admin/feature-flags?fresh=1' : '/admin/feature-flags';
      const resp = await httpClient().get<EvaluatedClientFlags>(url);
      // Identity changed while this request was in flight — discard the
      // response for the outgoing org rather than writing it into the new one.
      if (token !== flagsToken) return;
      const parsed = EvaluatedClientFlagsSchema.safeParse(resp.data);
      if (parsed.success) {
        flags.value = parsed.data;
        flagsLoaded.value = true;
      } else {
        // Malformed payload — leave safe defaults; a later navigation retries.
        console.error('[adminSession] invalid feature-flag payload', parsed.error);
      }
    } catch (e) {
      if (token !== flagsToken) return;
      console.error('[adminSession] failed to load feature flags', e);
    } finally {
      // Only release the loading flag if we still own the current load; after a
      // reset a newer load (or none) owns it — don't clobber it.
      if (token === flagsToken) flagsLoading.value = false;
    }
  }

  // Idempotent: skips when already loaded/loading. Used on navigation.
  async function loadFeatureFlags(): Promise<void> {
    if (flagsLoaded.value || flagsLoading.value) return;
    await fetchFlagsForToken();
  }

  // Force a refetch regardless of loaded/loading state (e.g. a WS
  // feature_flag.changed frame). fetchFlagsForToken mints a fresh token so a
  // response arriving after an identity change (or a newer reload) is discarded,
  // and the same swallow-errors / default-false behavior applies.
  async function reloadFeatureFlags(): Promise<void> {
    await fetchFlagsForToken(true);
  }

  const moveToNextBidder = computed(() => flags.value?.['settlement.move-to-next-bidder'] ?? false);
  const activityFeed = computed(() => flags.value?.['settlement.activity-feed'] ?? false);
  // Card-present terminal payments (GT1-327). Gates the "Credit Card Terminal"
  // method in the settlement console; defaults false until flags load.
  const terminalPayments = computed(() => flags.value?.['payments.terminal'] ?? false);
  // Admin bid-void (GT1-442). Gates the per-bid Void control in the run-live Bid
  // History surface; defaults false until flags load, so a dark-launch feature is
  // never revealed by an unloaded/failed flag fetch. The server's own gate on
  // POST /admin/bids/:bidId/void (403 VOID_DISABLED) is the authoritative
  // backstop — this is only the affordance half.
  const bidVoid = computed(() => flags.value?.['bids.void'] ?? false);
    const placeForBidder = computed(() => flags.value?.['bids.place-for-bidder'] ?? false);
    const sellToBidder = computed(() => flags.value?.['settlement.sell-to-bidder'] ?? false);

  function setSession(token: string, me: AdminMe): void {
    // Flags are org-scoped, so a change of org must not keep the previous
    // org's answers. But setSession is ALSO how the same identity is renewed:
    // the keep-alive timer refreshes the JWT through adoptJwt, and
    // refreshDisplayInfo re-writes the cached display name. Resetting on those
    // dropped every flag to its `?? false` default and nothing refetched —
    // loadFeatureFlags is idempotent and only runs on navigation — so a
    // flag-gated control silently disappeared mid-session and came back on a
    // hard refresh. Reset only when the ORG actually moves, and refetch when
    // it does rather than leaving a hole.
    const orgChanged = user.value?.orgId !== me.orgId;
    jwt.value = token;
    user.value = me;
    supervisorUser.value = null;
    if (orgChanged) {
      resetFlags();
      // eslint-disable-next-line local-rules/no-floating-side-effect -- a
      // synchronous store action cannot await; the fetch swallows its own
      // errors and leaves safe defaults, and every caller of setSession is a
      // navigation or a timer with nothing to hold open.
      void loadFeatureFlags();
    }
    writeSessionCookie({ jwt: token, user: me });
    // A supervisor assuming an org keeps their root identity (the lobby
    // round-trip re-adopts it); any other login replaces the whole identity.
    if (decodeJwtPayload(token)?.is_supervisor !== true) {
      clearSupervisorRootCookie();
    }
  }

  function setSupervisorSession(token: string, supervisor: SupervisorMe): void {
    jwt.value = token;
    user.value = null;
    supervisorUser.value = supervisor;
    resetFlags();
    clearSessionCookie();
    writeSupervisorRootCookie({ jwt: token, supervisorUser: supervisor });
  }

  /**
   * Re-adopt the supervisor's root identity (returning to the lobby from an
   * assumed org). Returns false when no valid root exists.
   */
  function restoreSupervisorRoot(): boolean {
    const root = readRoot();
    if (!root) return false;
    jwt.value = root.jwt;
    user.value = null;
    supervisorUser.value = root.supervisorUser;
    resetFlags();
    clearSessionCookie();
    return true;
  }

  function getJwt(): string | null {
    return jwt.value;
  }

  function clear(): void {
    jwt.value = null;
    user.value = null;
    supervisorUser.value = null;
    resetFlags();
    clearSessionCookie();
    clearSupervisorRootCookie();
  }

  return {
    jwt,
    user,
    supervisorUser,
    isAuthenticated,
    isSupervisor,
    canSwitchOrg,
    role,
    orgId,
    setSession,
    setSupervisorSession,
    restoreSupervisorRoot,
    getJwt,
    clear,
    flags,
    flagsLoaded,
    flagsLoading,
    loadFeatureFlags,
    reloadFeatureFlags,
    moveToNextBidder,
    activityFeed,
    terminalPayments,
    bidVoid,
    placeForBidder,
    sellToBidder,
  };
});
